import React from "react";
import { Box } from "Components/Base/Layout/Box";
import BaseGrid from "Components/Fragment/BaseGrid";
import { Typography } from "Components/Base/DataDisplay/Typography";
import { Link } from "Components/Base/Navigation/Link";
import { LearningActivityListItem } from "Components/Module/LearningActivityItem";
import { LearningActivityCardLink } from "Components/Module/LearningActivityItem/Actions";
import { CardMedia } from "Components/Base/Surfaces/Card/CardMedia";
import ActivityDescription from "Components/Module/LearningActivityItem/Description";
import ActivityCategories from "Components/Module/LearningActivityItem/Categories";
import {
  CardNoImage,
  CardFallbackImage,
} from "Components/Module/LearningActivityItem/functions";
import { RatingChip } from "Components/Module/LearningActivityItem/Chips";
import { CARD_IMAGE_HEIGHT_THUMBNAIL } from "Components/Module/LearningActivityItem/constants";
import { useFilterContext, useUIContext } from "../../context";
import { isOnlineLearningActivityType } from "../../constants";
import { getReadMoreLink } from "../../utils";

const CourseListItemComponent = ({
  learningActivity,
  languageContext,
  state,
  imageErrors,
  onImageError,
}) => {
  const {
    id,
    imageUrl,
    title,
    type,
    numberOfRatings,
    averageRating,
    description,
    shortDescription,
    categories,
    learningActivityUrl,
    learningActivityExternalUrl,
  } = learningActivity;

  const { filters } = useFilterContext();
  const { uiState } = useUIContext();

  const readMoreLink = getReadMoreLink({
    learningActivityUrl,
    learningActivityExternalUrl,
    isPublic: state.isPublic,
    filters,
    uiState,
  });

  const hasError = imageErrors.has(id);
  const validImage = state.displayImage && imageUrl && imageUrl.trim() !== "";
  const imageLinked = Boolean(readMoreLink);

  let imageComponent;
  if (hasError) {
    imageComponent = <CardFallbackImage title={imageLinked ? "" : title} />;
  } else if (validImage) {
    imageComponent = (
      <CardMedia
        component="img"
        height={CARD_IMAGE_HEIGHT_THUMBNAIL}
        image={imageUrl}
        alt={imageLinked ? "" : title}
        loading="lazy"
        onError={() => onImageError(id)}
      />
    );
  } else {
    imageComponent = <CardNoImage />;
  }

  const readMoreTooltip = `${languageContext("readmoreinfo")} ${title}`;
  const readMoreImageAriaLabel = `${languageContext("readmoreCourse")} ${title}`;

  const imageComponentForList = readMoreLink ? (
    <Link
      href={readMoreLink}
      aria-label={readMoreImageAriaLabel}
      title={readMoreTooltip}
      underline="none"
      sx={{ display: "block", color: "inherit" }}
    >
      {imageComponent}
    </Link>
  ) : (
    imageComponent
  );

  const activityDescription = isOnlineLearningActivityType(type)
    ? shortDescription || description
    : description;

  const showRating =
    state.displayRating &&
    Number(averageRating) > 0 &&
    Number(numberOfRatings) > 0;

  return (
    <LearningActivityListItem
      id={`learningActivityCard${id}`}
      key={`learningActivityList-${id}`}
      title={title}
      titleLink={
        readMoreLink
          ? {
              href: readMoreLink,
              linkTitle: readMoreTooltip,
            }
          : undefined
      }
      type={type}
      displayImage={validImage}
      displayIcon={false}
      imageComponent={imageComponentForList}
      chips={
        showRating ? (
          <RatingChip
            label={`${averageRating} (${numberOfRatings})`}
            ariaLabel={`${languageContext("ratingAccessible")}: ${averageRating} ${languageContext("outOfFive")}, ${numberOfRatings} ${languageContext("ratingsCount")}`}
          />
        ) : null
      }
      content={
        <>
          {state.listView && <ActivityCategories categories={categories} />}
          <BaseGrid childWidth="auto">
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="h4" sx={{ fontWeight: "normal" }}>
                {languageContext(
                  isOnlineLearningActivityType(type) ? "onlinecourse" : type,
                )}
              </Typography>
            </Box>
          </BaseGrid>
          <ActivityDescription
            description={activityDescription}
            maxLength={state.listView ? 10000 : 100}
          />
        </>
      }
      actions={
        <>
          <LearningActivityCardLink href={readMoreLink} title={readMoreTooltip}>
            {languageContext("readmore")}
          </LearningActivityCardLink>
        </>
      }
    />
  );
};

const CourseListItem = React.memo(
  CourseListItemComponent,
  (prevProps, nextProps) => {
    // Only re-render if relevant props have changed
    return (
      prevProps.learningActivity.id === nextProps.learningActivity.id &&
      prevProps.learningActivity.imageUrl ===
        nextProps.learningActivity.imageUrl &&
      prevProps.learningActivity.title === nextProps.learningActivity.title &&
      prevProps.learningActivity.type === nextProps.learningActivity.type &&
      prevProps.learningActivity.description ===
        nextProps.learningActivity.description &&
      prevProps.learningActivity.shortDescription ===
        nextProps.learningActivity.shortDescription &&
      prevProps.learningActivity.averageRating ===
        nextProps.learningActivity.averageRating &&
      prevProps.learningActivity.numberOfRatings ===
        nextProps.learningActivity.numberOfRatings &&
      prevProps.learningActivity.learningActivityUrl ===
        nextProps.learningActivity.learningActivityUrl &&
      prevProps.learningActivity.learningActivityExternalUrl ===
        nextProps.learningActivity.learningActivityExternalUrl &&
      prevProps.state.listView === nextProps.state.listView &&
      prevProps.state.displayImage === nextProps.state.displayImage &&
      prevProps.state.displayRating === nextProps.state.displayRating &&
      prevProps.state.isPublic === nextProps.state.isPublic &&
      prevProps.languageContext === nextProps.languageContext &&
      prevProps.imageErrors.has(prevProps.learningActivity.id) ===
        nextProps.imageErrors.has(nextProps.learningActivity.id)
    );
  },
);

CourseListItem.displayName = "CourseListItem";

export default CourseListItem;
